import React, { ComponentType, lazy } from "react";

const RELOADED_KEY = "sh_chunk_reloaded";

const isChunkError = (err: any): boolean =>
  err?.name === "ChunkLoadError" || /Loading (CSS )?chunk|dynamically imported module/i.test(String(err?.message || ""));

const clearReloadMark = () => {
  try {
    window.sessionStorage.removeItem(RELOADED_KEY);
  } catch {
    /* storage blocked — nothing to clear */
  }
};

/**
 * A tab opened before a release still holds the old build's chunk names, so
 * its first lazy import after the deploy 404s (ChunkLoadError). Reload once to
 * pick up the new build. If the chunk still fails right after that reload — or
 * storage is blocked and we can't tell — let the error reach the error
 * boundary instead of looping.
 */
const reloadOnceOnChunkError = (err: unknown): Promise<never> => {
  if (!isChunkError(err)) throw err;
  try {
    if (window.sessionStorage.getItem(RELOADED_KEY) === "1") throw err;
    window.sessionStorage.setItem(RELOADED_KEY, "1");
  } catch {
    throw err;
  }
  window.location.reload();
  return new Promise<never>(() => {}); // the reload takes over
};

export type PreloadableComponent<P> = ((props: P) => React.ReactElement) & { preload: () => Promise<void> };

/**
 * React.lazy with two additions:
 *  - `preload()` fetches the code ahead of time; once it's in, the component
 *    renders straight away instead of suspending (React.lazy alone always
 *    suspends on first render, flashing the fallback for a frame).
 *  - a deploy-proof load (see reloadOnceOnChunkError). Only a render-time load
 *    may reload the page; a failed background preload is just forgotten, so
 *    the next attempt starts fresh.
 */
export function lazyWithPreload<P extends object>(factory: () => Promise<{ default: ComponentType<P> }>): PreloadableComponent<P> {
  let loaded: ComponentType<P> | null = null;
  let pending: Promise<{ default: ComponentType<P> }> | null = null;

  const load = () => {
    if (!pending) {
      pending = factory().then(
        (mod) => {
          loaded = mod.default;
          clearReloadMark();
          return mod;
        },
        (err) => {
          pending = null;
          throw err;
        }
      );
    }
    return pending;
  };

  const Lazy = lazy(() => load().catch(reloadOnceOnChunkError));

  const Preloadable = ((props: P) => React.createElement((loaded ?? Lazy) as ComponentType<P>, props)) as PreloadableComponent<P>;
  Preloadable.preload = () => load().then(() => undefined);
  return Preloadable;
}
